import type { NoteNamingStyle } from './preferences';

export const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;
export const FLAT_NAMES = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'] as const;

const SOLFEGE: Record<string, string> = {
  C: 'do',
  'C#': 'do#',
  Db: 'reb',
  D: 're',
  'D#': 're#',
  Eb: 'mib',
  E: 'mi',
  F: 'fa',
  'F#': 'fa#',
  Gb: 'solb',
  G: 'sol',
  'G#': 'sol#',
  Ab: 'lab',
  A: 'la',
  'A#': 'la#',
  Bb: 'sib',
  B: 'si',
};

export interface ParsedPitch {
  letter: string;
  accidental: '' | '#' | 'b';
  octave: number;
  midi: number;
}

/** Standard A4=440 Hz → MIDI mapping. */
export function midiToFreq(midi: number): number {
  return Math.round(440 * Math.pow(2, (midi - 69) / 12));
}

export function freqToMidi(freq: number): number {
  return 69 + 12 * Math.log2(freq / 440);
}

export function midiToName(midi: number): { letter: string; accidental: '' | '#'; octave: number } {
  const rounded = Math.round(midi);
  const noteIdx = ((rounded % 12) + 12) % 12;
  const name = SHARP_NAMES[noteIdx];
  const octave = Math.floor(rounded / 12) - 1;
  const letter = name[0];
  const accidental = (name.length > 1 ? '#' : '') as '' | '#';
  return { letter, accidental, octave };
}

export function formatDetectedNote(
  freq: number,
  style: NoteNamingStyle
): { label: string; midi: number; cents: number } {
  const midi = freqToMidi(freq);
  const rounded = Math.round(midi);
  const cents = Math.round((midi - rounded) * 100);
  const { letter, accidental, octave } = midiToName(rounded);
  const base = `${letter}${accidental}`;
  const label =
    style === 'dorm'
      ? `${SOLFEGE[base] ?? base}${octave}`
      : `${base}${octave}`;
  return { label, midi: rounded, cents };
}

/** Format an OSMD ToStringShort value like "A4", "F#5", "Bb3" per user preference. */
export function formatScoreNote(shortName: string, style: NoteNamingStyle): string {
  if (!shortName) return '—';
  const match = shortName.match(/^([A-Ga-g])([#b]?)(-?\d+)$/);
  if (!match) return shortName;
  const [, letterRaw, accidental, octave] = match;
  const letter = letterRaw.toUpperCase();
  const base = `${letter}${accidental}`;
  if (style === 'dorm') {
    return `${SOLFEGE[base] ?? base.toLowerCase()}${octave}`;
  }
  return `${base}${octave}`;
}

/** Parse "A4", "F#5", "Bb3" into midi. */
export function scoreNoteToMidi(shortName: string): number | null {
  const m = shortName.match(/^([A-Ga-g])([#b]?)(-?\d+)$/);
  if (!m) return null;
  const [, letterRaw, accidental, octaveStr] = m;
  const letter = letterRaw.toUpperCase();
  const letterSemis: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  const base = letterSemis[letter];
  if (base === undefined) return null;
  const acc = accidental === '#' ? 1 : accidental === 'b' ? -1 : 0;
  const octave = parseInt(octaveStr, 10);
  return (octave + 1) * 12 + base + acc;
}
